import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AuthSession } from '../../auth';
import { truncateNick } from '../../utils/truncateNick';
import { LANGUAGE, SUPPORTED_LANGUAGES, setLanguage, tr } from '../../i18n';
import OnlineCounter from './OnlineCounter';
import './AppHeader.css';

export type AppHeaderNavItem = {
  to: string;
  label: string;
  requiresAuth?: boolean;
  requiresPermission?: string;
  /** Акцентная подача пункта в навигации (градиентная пилюля). */
  highlight?: boolean;
};

export type AppHeaderNavGroup = {
  id: string;
  label: string;
  adminOnly: boolean;
  items: AppHeaderNavItem[];
};

type AppHeaderProps = {
  navGroups: AppHeaderNavGroup[];
  onLogout: () => void;
  onLoginClick: () => void;
  session: AuthSession | null;
  showOnlineCounter?: boolean;
};

type MenuId = 'admin' | 'profile' | 'mobile';

const SCROLLED_OFFSET = 20;

const ChevronIcon: React.FC = () => (
  <svg className="app-header-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BurgerIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path
      d={open ? 'M4 4l12 12M16 4 4 16' : 'M3 5.5h14M3 10h14M3 14.5h14'}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const navLinkClass = (base: string, highlight = false) => ({ isActive }: { isActive: boolean }) =>
  `${base}${highlight ? ` ${base}--highlight` : ''}${isActive ? ' active' : ''}`;

const AppHeader: React.FC<AppHeaderProps> = ({ navGroups, onLogout, onLoginClick, session, showOnlineCounter = false }) => {
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [scrolled, setScrolled] = useState(() => window.scrollY > SCROLLED_OFFSET);

  const visibleGroups = navGroups.filter(group => group.items.length > 0);
  const mainItems = visibleGroups.filter(group => !group.adminOnly).flatMap(group => group.items);
  const adminGroup = visibleGroups.find(group => group.adminOnly);
  const adminActive = Boolean(adminGroup?.items.some(item => pathname.startsWith(item.to)));

  const isAuth = Boolean(session?.authenticated);
  const displayName = session?.displayName || session?.playerUid || 'Player';
  const initials = displayName.slice(0, 1).toUpperCase();
  const steamId = session?.steamId || '';
  const avatarUrl = session?.avatarUrl || '';
  const profileTo = session?.playerUid ? `/players/${encodeURIComponent(session.playerUid)}` : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLLED_OFFSET);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
  }, [pathname]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const root = (event.target as Element | null)?.closest<HTMLElement>('[data-menu]');
      if (root?.dataset.menu !== openMenu) setOpenMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpenMenu(null);
      document.querySelector<HTMLElement>(`[data-menu="${openMenu}"] [data-menu-trigger]`)?.focus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu]);

  const toggleMenu = useCallback((id: MenuId) => {
    setOpenMenu(current => (current === id ? null : id));
  }, []);

  const handleLogout = () => {
    setOpenMenu(null);
    onLogout();
  };

  const languageSwitcher = (
    <div className="app-header-lang" role="group" aria-label={tr.common.sidebar.languageSwitcher}>
      {SUPPORTED_LANGUAGES.map(language => (
        <button
          key={language}
          type="button"
          className={`app-header-lang-btn${language === LANGUAGE ? ' active' : ''}`}
          aria-pressed={language === LANGUAGE}
          onClick={() => setLanguage(language)}
        >
          {language.toUpperCase()}
        </button>
      ))}
    </div>
  );

  const avatar = (
    <span className="app-header-avatar-wrap">
      {avatarUrl ? (
        <img className="app-header-avatar" src={avatarUrl} alt="" />
      ) : (
        <span className="app-header-avatar app-header-avatar-placeholder">{initials}</span>
      )}
      <span className="app-header-online-dot" role="img" aria-label={tr.common.sidebar.online} />
    </span>
  );

  return (
    <header className={`app-header-bar${scrolled || openMenu === 'mobile' ? ' app-header-bar--solid' : ''}`}>
      <div className="app-header-inner">
        <NavLink className="app-header-brand" to="/" aria-label="Ratten Run">
          <span className="brand-mark">RR</span>
          <strong>Ratten Run</strong>
        </NavLink>

        <nav className="app-header-nav" aria-label={tr.common.sidebar.mainNavigation}>
          {mainItems.map(item => (
            <NavLink key={item.to} to={item.to} className={navLinkClass('app-header-link', item.highlight)}>
              {item.label}
            </NavLink>
          ))}

          {adminGroup && (
            <div className="app-header-menu" data-menu="admin">
              <button
                type="button"
                data-menu-trigger
                className={`app-header-link app-header-link-button${adminActive ? ' active' : ''}${openMenu === 'admin' ? ' open' : ''}`}
                aria-expanded={openMenu === 'admin'}
                aria-haspopup="true"
                title={tr.common.sidebar.adminMenu}
                onClick={() => toggleMenu('admin')}
              >
                {adminGroup.label}
                <ChevronIcon />
              </button>
              {openMenu === 'admin' && (
                <div className="app-header-dropdown">
                  {adminGroup.items.map(item => (
                    <NavLink key={item.to} to={item.to} className={navLinkClass('app-header-dropdown-item')}>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="app-header-spacer" />

        <div className="app-header-actions">
          {showOnlineCounter && <OnlineCounter />}
          <div className="app-header-lang-desktop">{languageSwitcher}</div>

          {isAuth ? (
            <div className="app-header-menu" data-menu="profile">
              <button
                type="button"
                data-menu-trigger
                className={`app-header-chip${openMenu === 'profile' ? ' open' : ''}`}
                aria-expanded={openMenu === 'profile'}
                aria-haspopup="true"
                aria-label={tr.common.sidebar.profileMenu}
                title={displayName}
                onClick={() => toggleMenu('profile')}
              >
                {avatar}
                <span className="app-header-chip-name">{truncateNick(displayName)}</span>
                <ChevronIcon />
              </button>
              {openMenu === 'profile' && (
                <div className="app-header-dropdown app-header-dropdown-end">
                  {steamId && (
                    <div className="app-header-dropdown-meta" title={steamId}>{steamId}</div>
                  )}
                  <NavLink
                    to={profileTo}
                    className={navLinkClass('app-header-dropdown-item')}
                    title={tr.common.sidebar.profileTitle}
                  >
                    {tr.common.sidebar.profile}
                  </NavLink>
                  <button type="button" className="app-header-dropdown-item app-header-dropdown-danger" onClick={handleLogout}>
                    {tr.common.sidebar.logout}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                className="app-header-steam-btn"
                aria-label={tr.common.sidebar.loginWithSteam}
                onClick={onLoginClick}
              >
                <span className="app-header-steam-icon" aria-hidden="true">S</span>
                <span className="app-header-steam-label">{tr.common.sidebar.loginWithSteam}</span>
              </button>
            </>
          )}

          <span className="app-header-burger-wrap" data-menu="mobile">
            <button
              type="button"
              data-menu-trigger
              className="app-header-burger"
              aria-expanded={openMenu === 'mobile'}
              aria-controls="app-header-mobile-panel"
              aria-label={openMenu === 'mobile' ? tr.common.sidebar.closeMenu : tr.common.sidebar.openMenu}
              onClick={() => toggleMenu('mobile')}
            >
              <BurgerIcon open={openMenu === 'mobile'} />
            </button>
          </span>
        </div>
      </div>

      {openMenu === 'mobile' && (
        <div className="app-header-mobile" id="app-header-mobile-panel" data-menu="mobile">
          <nav className="app-header-mobile-inner" aria-label={tr.common.sidebar.siteNavigation}>
            {mainItems.map(item => (
              <NavLink key={item.to} to={item.to} className={navLinkClass('app-header-mobile-link', item.highlight)}>
                {item.label}
              </NavLink>
            ))}

            {adminGroup && (
              <>
                <div className="app-header-mobile-title">{adminGroup.label}</div>
                {adminGroup.items.map(item => (
                  <NavLink key={item.to} to={item.to} className={navLinkClass('app-header-mobile-link')}>
                    {item.label}
                  </NavLink>
                ))}
              </>
            )}

            <div className="app-header-mobile-footer">
              {languageSwitcher}
              <NavLink to="/privacy" className="app-header-mobile-legal">
                {tr.common.sidebar.privacyPolicy}
              </NavLink>
              {isAuth && (
                <button type="button" className="app-header-mobile-logout" onClick={handleLogout}>
                  {tr.common.sidebar.logout}
                </button>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default AppHeader;
